/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    /**
     * Sætter src på tilvalgskortenes billeder (data-drawer-src). De udsendes uden URL, så intet
     * hentes på sidevisninger hvor draweren aldrig åbnes. Kaldes ved åbning og efter gen-render.
     */
    hydrateImages() {
      this.querySelectorAll('img[data-drawer-src]').forEach((img) => {
        if (img.dataset.drawerSrcset) img.srcset = img.dataset.drawerSrcset;
        img.src = img.dataset.drawerSrc;
        img.removeAttribute('data-drawer-src');
        img.removeAttribute('data-drawer-srcset');
      });
    }

    /**
     * "✓ Lagt i kurven"-stribe nederst på linjen der lige er tilføjet; forsvinder igen efter
     * ~3 sek. Erstatter den tidligere header-titel, som ikke var plads til på alle sprog.
     * @param {Element} row - Kurvlinjen (.cart-item).
     */
    showAddedStripe(row) {
      row.querySelectorAll('.cart-item__added').forEach((el) => el.remove());
      const stripe = document.createElement('div');
      stripe.className = 'cart-item__added';
      stripe.setAttribute('role', 'status');
      stripe.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="currentColor"/><path d="M7.2 12.4l3.2 3.2 6.4-6.6" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg><span></span>';
      stripe.querySelector('span').textContent = (this.dataset.addedText || '').replace(/^✓\s*/, '');
      row.appendChild(stripe);
      setTimeout(() => stripe.classList.add('is-hiding'), 3000);
      setTimeout(() => stripe.remove(), 3500);
    }

    /**
     * Den tilføjede linje toner ind, og alt under den (øvrige linjer, tilvalg) glider ned på
     * plads — som om linjen skubber dem. Var varen der i forvejen (antal +1), blinker linjen kun.
     * Kører efter drawerens slide-in er godt i gang. Respekterer prefers-reduced-motion.
     * @param {string|number} variantId - Varianten der blev lagt i kurven.
     * @param {boolean} isNew - Om linjen er ny i kurven.
     */
    animateAddedLine(variantId, isNew) {
      if (!variantId) return;
      const row = this.querySelector(`.cart-item[data-variant-id="${variantId}"]`);
      if (!row) return;
      this.showAddedStripe(row);

      if (!Element.prototype.animate) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const easing = 'cubic-bezier(.2,.8,.2,1)';

      if (!isNew) {
        row.animate(
          [{ backgroundColor: 'rgba(114, 163, 0, .14)' }, { backgroundColor: 'rgba(114, 163, 0, 0)' }],
          { duration: 1400, delay: 250, easing: 'ease-out', fill: 'backwards' }
        );
        return;
      }

      const group = [row];
      if (row.nextElementSibling && row.nextElementSibling.classList.contains('cart-item__stock-row')) {
        group.push(row.nextElementSibling);
      }
      const height = group.reduce((sum, el) => sum + el.getBoundingClientRect().height, 0);

      // Alt der ligger under linjen i scroll-området.
      const below = [];
      let next = group[group.length - 1].nextElementSibling;
      while (next) { below.push(next); next = next.nextElementSibling; }
      ['#cart-drawer-addons-or-collection', '.cart-drawer__trust'].forEach((sel) => {
        const el = this.querySelector(sel);
        if (el) below.push(el);
      });

      const timing = { duration: 500, delay: 280, easing, fill: 'backwards' };
      group.forEach((el) => el.animate(
        [{ opacity: 0, transform: 'translateY(-10px)' }, { opacity: 1, transform: 'none' }],
        timing
      ));
      below.forEach((el) => el.animate(
        [{ transform: `translateY(-${height}px)` }, { transform: 'none' }],
        timing
      ));
    }

    /**
     * Pilene i tilvalgskarrusellen (kun synlige ved mus).
     * @param {object} evt - Event object.
     */
    handleUpsellNav(evt) {
      const btn = evt.target.closest && evt.target.closest('[data-upsell-nav]');
      if (!btn) return;
      const track = this.querySelector('[data-upsell-track]');
      if (!track) return;
      track.scrollBy({ left: Number(btn.dataset.upsellNav) * track.clientWidth * 0.8, behavior: 'smooth' });
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.addEventListener('click', this.handleUpsellNav.bind(this));

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // "Lagt i kurven"-striben gælder kun den åbning, renderContents bestilte efter et tilføj.
      const added = this.justAddedPending;
      this.justAddedPending = null;
      this.hydrateImages();

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';

        // Indholdet er display:none indtil [open] — mål og animér først i næste frame.
        if (added) requestAnimationFrame(() => this.animateAddedLine(added.id, added.isNew));
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      // product-form.js sætter data-just-added (variant-id) og data-just-added-new før render.
      const added = this.dataset.justAdded
        ? { id: this.dataset.justAdded, isNew: this.dataset.justAddedNew !== 'false' }
        : null;
      delete this.dataset.justAdded;
      delete this.dataset.justAddedNew;

      if (openDrawer && this.getAttribute('open') === null) {
        this.justAddedPending = added;
        setTimeout(() => this.open());
      } else if (this.getAttribute('open') !== null) {
        this.hydrateImages();
        if (added) this.animateAddedLine(added.id, added.isNew);
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

/**
 * "Bestil inden 3t 31m og 17s for levering i morgen" i kurvens opsummering.
 * data-mode="today" (tunge varer eller andre lande end DK): "…så afsender vi din ordre i dag" —
 * vises blot før dagens afsendelsestid; leveringsrækken bliver stående (ingen data-cutoff-fallback).
 *
 * Renderes kun når alle varer er på eget lager i DK (cart-order-delivery.liquid). Viser sig
 * selv og skjuler leveringsrækken ([data-cutoff-fallback]) KUN når levering i morgen kan nås:
 * dagens afsendelsestid er ikke passeret, i dag er ikke lukkedag, og i morgen er en hverdag
 * der ikke er lukkedag. Ellers står leveringsrækken ("1-2 hverdage") urørt.
 * Tiden regnes altid i dansk tid. Tæller kun mens draweren er åben.
 */
if (!customElements.get('cart-cutoff-countdown')) {
  class CartCutoffCountdown extends HTMLElement {
    connectedCallback() {
      try {
        this.closed = new Set(JSON.parse(this.dataset.closed || '[]'));
        this.hours = JSON.parse(this.dataset.hours || '{}');
      } catch (e) {
        return;
      }
      this.timeEls = this.querySelectorAll('[data-cutoff-time]');
      this.msgTomorrow = this.querySelector('[data-cutoff-msg="tomorrow"]');
      this.msgToday = this.querySelector('[data-cutoff-msg="today"]');
      this.fallback = this.parentElement && this.parentElement.querySelector('[data-cutoff-fallback]');
      this.drawer = this.closest('cart-drawer');
      this.tick();
      this.timer = setInterval(() => {
        if (!this.drawer || this.drawer.hasAttribute('open')) this.tick();
      }, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    /** Nu i dansk tid: { iso: 'YYYY-MM-DD', weekday: 0-6, seconds: sekunder siden midnat, date } */
    static nowDK() {
      const parts = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).forEach((p) => { parts[p.type] = p.value; });
      const date = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day));
      return {
        iso: date.toISOString().slice(0, 10),
        weekday: date.getUTCDay(),
        seconds: (+parts.hour) * 3600 + (+parts.minute) * 60 + (+parts.second),
        date
      };
    }

    show(visible) {
      this.hidden = !visible;
      // Leveringsrækken skjules kun når der står "levering i morgen" — "afsender i dag" siger
      // ikke hvornår varen er fremme, så her skal den forventede leveringstid blive stående.
      if (this.fallback) this.fallback.hidden = visible && this.fallbackHidden !== false;
    }

    tick() {
      const now = CartCutoffCountdown.nowDK();
      const close = this.hours[now.weekday];
      if (!close || this.closed.has(now.iso) || !this.timeEls.length) { this.show(false); return; }

      const [ch, cm] = close.split(':').map(Number);
      const left = ch * 3600 + cm * 60 - now.seconds;

      const tomorrow = new Date(now.date.getTime() + 86400000);
      const tomorrowIso = tomorrow.toISOString().slice(0, 10);
      const tomorrowDay = tomorrow.getUTCDay();
      // Pakkeshop leverer mandag-lørdag (ikke søndag og ikke på lukkedage).
      const deliversTomorrow = tomorrowDay >= 1 && tomorrowDay <= 6 && !this.closed.has(tomorrowIso);

      if (left <= 0) { this.show(false); return; }

      // "Levering i morgen" kun i tomorrow-mode og når morgendagen er en leveringsdag; ellers
      // "afsender i dag" (tunge varer, udland, eller fx lørdag → søndag).
      const showTomorrow = this.dataset.mode !== 'today' && deliversTomorrow;
      if (this.msgTomorrow) this.msgTomorrow.hidden = !showTomorrow;
      if (this.msgToday) this.msgToday.hidden = showTomorrow;
      this.fallbackHidden = showTomorrow;

      const h = Math.floor(left / 3600);
      const m = Math.floor((left % 3600) / 60);
      const sec = left % 60;
      const format = h > 0 ? this.dataset.format : this.dataset.formatShort;
      const text = format.replace('[h]', h).replace('[m]', m).replace('[s]', sec);
      this.timeEls.forEach((el) => { el.textContent = text; });
      this.show(true);
    }
  }

  customElements.define('cart-cutoff-countdown', CartCutoffCountdown);
}
